"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CommerceAPI = void 0;
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var sdk = _interopRequireWildcard(require("commerce-sdk-isomorphic"));
var _url = require("@salesforce/pwa-kit-react-sdk/utils/url");
var _shopperBaskets = require("./shopper-baskets");
var _ocapiProducts = require("./ocapi-products");
var _ocapiShopperOrders = require("./ocapi-shopper-orders");
var _ocapiShopperPassword = require("./ocapi-shopper-password");
var _utils = require("./utils");
var _auth = require("./auth");
var _einstein = require("./custom/einstein");
var _ocapiShopperStores = require("./ocapi-shopper-stores");
var _ocapiShopperContext = require("./ocapi-shopper-context");
var _scapiSitesApi = require("./scapi-sites-api");
var _scapiBrazeApi = require("./custom/scapi-braze-api");
var _scapiFriendlycaptchaApi = require("./custom/scapi-friendlycaptcha-api");
const _excluded = ["proxyPath"];
/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/* eslint-disable no-unused-vars */
// @ts-ignore
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * The configuration details for the connecting to the API.
 * @typedef {Object} ClientConfig
 * @property {string} [proxy] - URL to proxy fetch calls through.
 * @property {string} [headers] - Request headers to be added to requests.
 * @property {Object} [parameters] - API connection parameters for SDK.
 * @property {string} [parameters.clientId]
 * @property {string} [parameters.organizationId]
 * @property {string} [parameters.shortCode]
 * @property {string} [parameters.siteId]
 * @property {string} [parameters.version]
 */

/**
 * An object containing the customer's login credentials.
 * @typedef {Object} CustomerCredentials
 * @property {string} credentials.email
 * @property {string} credentials.password
 */

/**
 * Salesforce Customer object.
 * {@link https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customer}}
 * @typedef {Object} Customer
 */

/**
 * A wrapper class that proxies calls to the underlying commerce-sdk-isomorphic.
 * The sdk class instances are created automatically with the given config.
 */
class CommerceAPI {
  /**
   * Create an instance of the API with the given config.
   * @param {ClientConfig} config - The config used to instantiate SDK apis.
   */
  constructor(config = {}) {
    var _this$_config$einstei;
    const {
        proxyPath
      } = config,
      restConfig = (0, _objectWithoutProperties2.default)(config, _excluded);

    // Client-side requests should be proxied via the configured path.
    const proxy = `${(0, _url.getAppOrigin)()}${proxyPath}`;
    this._config = _objectSpread({
      proxy
    }, restConfig);
    this.auth = new _auth.Auth(this);
    if ((_this$_config$einstei = this._config.einsteinConfig) !== null && _this$_config$einstei !== void 0 && _this$_config$einstei.einsteinId) {
      this.einstein = new _einstein.CustomEinsteinAPI(this);
    }

    // A mapping of property names to the SDK class constructors we'll be
    // providing instances for.
    //
    // NOTE: `sendLocale` and `sendCurrency` for sending locale and currency info to the API:
    // - boolean, if you want to affect _all_ methods for a given API
    // - OR an array (listing the API's methods), if you want to affect only certain methods of an API
    const apiConfigs = {
      shopperCustomers: {
        api: sdk.ShopperCustomers,
        sendLocale: false
      },
      shopperPassword: {
        api: _ocapiShopperPassword.OcapiShopperPassword
      },
      shopperBaskets: {
        api: _shopperBaskets.ShopperBaskets,
        sendLocale: false,
        sendCurrency: ['createBasket']
      },
      shopperExperience: {
        api: sdk.ShopperExperience
      },
      shopperGiftCertificates: {
        api: sdk.ShopperGiftCertificates
      },
      shopperLogin: {
        api: sdk.ShopperLogin,
        sendLocale: false
      },
      shopperOrders: {
        api: _ocapiShopperOrders.OcapiShopperOrders
      },
      shopperStores: {
        api: _ocapiShopperStores.OcapiShopperStores
      },
      shopperContext: {
        api: _ocapiShopperContext.OcapiShopperContext
      },
      sitesApi: {
        api: _scapiSitesApi.ScapiSitesApi
      },
      brazeApi: {
        api: _scapiBrazeApi.ScapiBrazeApi
      },
      friendlyCaptchaApi: {
        api: _scapiFriendlycaptchaApi.ScapiFriendlyCaptchaApi
      },
      shopperProducts: {
        api: sdk.ShopperProducts,
        sendCurrency: ['getProduct', 'getProducts']
      },
      shopperPromotions: {
        api: sdk.ShopperPromotions
      },
      shopperSearch: {
        api: sdk.ShopperSearch,
        sendCurrency: ['productSearch', 'getSearchSuggestions']
      },
      ocapiProducts: {
        api: _ocapiProducts.OcapiProducts
      }
    };

    // Instantiate the SDK class proxies and create getters from our api mapping.
    // The proxy handlers are called when accessing any of the mapped SDK class
    // proxies, executing various pre-defined hooks for tapping into or modifying
    // the outgoing method parameters and/or incoming SDK responses
    const self = this;
    Object.keys(apiConfigs).forEach(key => {
      const SdkClass = apiConfigs[key].api;
      // @ts-ignore
      self._sdkInstances = _objectSpread(_objectSpread({}, self._sdkInstances), {}, {
        [key]: new Proxy(new SdkClass(this._config), {
          get: function (obj, prop) {
            if (typeof obj[prop] === 'function') {
              return (...args) => {
                const fetchOptions = args[0];
                const {
                  locale,
                  currency
                } = self._config;
                if (fetchOptions !== null && fetchOptions !== void 0 && fetchOptions.ignoreHooks) {
                  return obj[prop](...args);
                }

                // Inject the locale and currency to the API call via its parameters.
                //
                // NOTE: The commerce sdk isomorphic will complain if you pass parameters to
                // it that it doesn't expect, this is why we only add the locale and currency
                // to some of the API calls.

                // By default we send the locale param and don't send the currency param.
                const {
                  sendLocale = true,
                  sendCurrency = false
                } = apiConfigs[key];
                const includeGlobalLocale = Array.isArray(sendLocale) ? sendLocale.includes(prop) : !!sendLocale;
                const includeGlobalCurrency = Array.isArray(sendCurrency) ? sendCurrency.includes(prop) : !!sendCurrency;
                fetchOptions.parameters = _objectSpread(_objectSpread(_objectSpread({}, includeGlobalLocale ? {
                  locale
                } : {}), includeGlobalCurrency ? {
                  currency
                } : {}), fetchOptions.parameters);

                // @ts-ignore
                return self.willSendRequest(prop, ...args).then(newArgs => {
                  return obj[prop](...newArgs).then(res => self.didReceiveResponse(res, newArgs));
                });
              };
            }
            return obj[prop];
          }
        })
      });
      Object.defineProperty(self, key, {
        get() {
          // @ts-ignore
          return self._sdkInstances[key];
        }
      });
    });
    this.getConfig = this.getConfig.bind(this);
  }

  /**
   * Returns the api client configuration
   * @returns {ClientConfig}
   */
  getConfig() {
    return this._config;
  }
  setInitialToken(authToken, refreshToken) {
    this._config.initialAuthToken = authToken;
    this._config.initialRefreshToken = refreshToken;
  }

  /**
   * Executed before every proxied method call to the SDK. Provides the method
   * name and arguments. This can be overidden in a subclass to perform any
   * logging or modifications to arguments before the request is sent.
   * @param {string} methodName - The name of the sdk method that will be called.
   * @param {...*} args - Original arguments for the SDK method.
   * @returns {Promise<Array>} - Updated arguments that will be passed to the SDK method
   */
  willSendRequest(methodName, ...params) {
    var _this = this;
    return (0, _asyncToGenerator2.default)(function* () {
      // We never need to modify auth request headers for these methods
      if (methodName === 'authenticateCustomer' || methodName === 'authorizeCustomer' || methodName === 'getAccessToken') {
        return params;
      }

      // If a login promise exists, we don't proceed unless it is resolved.
      const pendingLogin = _this.auth.pendingLogin;
      if (pendingLogin) {
        yield pendingLogin;
      }

      // If the token is invalid (missing, past/nearing expiration), we issue
      //  a login call, which will attempt to refresh the token or get a new
      //  guest token. Once login is complete, we can proceed.
      if (!_this.auth.isTokenValid) {
        // NOTE: Login will update `this.auth.authToken` with a fresh token
        // @ts-ignore
        yield _this.auth.login();
      }

      // Apply the appropriate auth headers and return new options
      const [fetchOptions, ...restParams] = params;
      const newFetchOptions = _objectSpread(_objectSpread({}, fetchOptions), {}, {
        headers: _objectSpread(_objectSpread({}, fetchOptions.headers), {}, {
          Authorization: _this.auth.authToken
        })
      });
      return [newFetchOptions, ...restParams];
    })();
  }

  /**
   * Executed when receiving a response from an SDK request. The response data
   * can be mutated or inspected before being passed back to the caller. Should
   * be overidden in a subclass.
   * @param {*} response - The response from the SDK method call.
   * @param {Array} args - Original arguments for the SDK method.
   * @returns {*} - The response to be passed back to original caller.
   */
  didReceiveResponse(response, args) {
    if ((0, _utils.isError)(response)) {
      if (response.title === 'Unauthorized') {
        // If an unauthorized error is returned, clear token from localStorage so in the next request, it gets regenerated
        // @ts-ignore
        this.auth.resetToken();
      }
      return _objectSpread(_objectSpread({}, response), {}, {
        isError: true,
        message: response.detail
      });
    }
    return response;
  }
}
exports.CommerceAPI = CommerceAPI;